import { takeLatest } from 'redux-saga/effects';
import { all, call, put, select } from 'typed-redux-saga';

import { FlagValuesMap } from '@/core/flags/flags.state';
import { waitForAndGetFlagValuesSaga } from '@/core/flags/sagas/waitForAndGetFlagValues.saga';
import { isB2C } from '@/core/settings/utils';
import { SProductFilters } from '@/generated/graphql/types';
import { brandSelector } from '@/productDiscovery/ProductPage/modules/Brand/brand.ducks';
import {
  DISABLE_SPONSORED_PRODUCTS,
  DisableSponsoredProducts,
  FETCH_SPONSORED_DATA,
  FetchSponsoredData,
  GET_SPONSORED_DATA_HIGHLIGHTED_PRODUCT_SUCCESS,
  GET_SPONSORED_DATA_PLAYLIST_SUCCESS,
  GET_SPONSORED_DATA_TOP_PRODUCT_SUCCESS,
  GetSponsoredDataHighlightedProductSuccess,
  GetSponsoredDataPlaylistSuccess,
  GetSponsoredDataTopProductSuccess,
  INIT_SPONSORED_DATA,
  InitSponsoredData,
} from '@/productDiscovery/ProductPage/modules/SponsoredData/sponsoredData.actions';
import {
  selectSponsoredProductsDisabled,
  selectTopProducts,
} from '@/productDiscovery/ProductPage/modules/SponsoredData/sponsoredData.reducer';
import { detailSelector } from '@/productDiscovery/ProductPage/selectors/detail';
import { MabayaProductWithModelId } from '@/productDiscovery/SponsoredProducts/interfaces/MabayaProduct';
import { getAbtParameterFromSaga } from '@/productDiscovery/SponsoredProducts/utils/getAbtParameter';
import {
  fetchSpDataProductPage,
  fetchSpDataProductPageHightlight,
  fetchSpDataProductPageTop,
} from '@/sellerAdsManagement/SponsoredProducts/services/fetchSpDataByProduct';
import { generateSpecificFiltersForBrand } from '@/sellerAdsManagement/SponsoredProducts/utils/generateSpecificFilters';

export function* fetchTopProductSponsoredData(
  productId: number,
  modelId: number,
  abt?: string,
  filters?: SProductFilters,
  isCrawler?: boolean,
  enableSpForCrawlersInPdp?: boolean,
) {
  if (isCrawler && !enableSpForCrawlersInPdp) {
    yield put<DisableSponsoredProducts>({
      type: DISABLE_SPONSORED_PRODUCTS,
    });
  }

  yield put<InitSponsoredData>({
    type: INIT_SPONSORED_DATA,
  });

  const topProductsSponsoredData =
    isB2C() && (!isCrawler || enableSpForCrawlersInPdp)
      ? yield* call(
          fetchSpDataProductPageTop,
          productId,
          modelId,
          'DESKTOP',
          abt,
          filters,
        )
      : ([] as MabayaProductWithModelId[]);

  yield put<GetSponsoredDataTopProductSuccess>({
    type: GET_SPONSORED_DATA_TOP_PRODUCT_SUCCESS,
    topProducts: topProductsSponsoredData,
  });
  return topProductsSponsoredData;
}

export function* fetchSponsoredData(action: FetchSponsoredData) {
  const HIGHLIGHTED_MIN_RATING = 4;
  const detail = yield* select(detailSelector);
  const brand = yield* select(brandSelector);
  const sponsoredProductsDisabled = yield* select(
    selectSponsoredProductsDisabled,
  );
  const topProducts = yield* select(selectTopProducts);

  if (detail?.productId && detail?.modelId && !sponsoredProductsDisabled) {
    const selectFlagValuesMap: FlagValuesMap = yield* call(
      waitForAndGetFlagValuesSaga,
    );
    const abt = getAbtParameterFromSaga(selectFlagValuesMap);

    const filters = generateSpecificFiltersForBrand(brand.id);
    const allSponsoredData = yield* all({
      highlightedProducts: call(
        fetchSpDataProductPageHightlight,
        detail.productId,
        detail.modelId,
        action.device,
        abt,
        {
          minRating: HIGHLIGHTED_MIN_RATING,
          ...filters,
        },
      ),
      sponsoredPlaylist: call(
        fetchSpDataProductPage,
        detail.productId,
        detail.modelId,
        action.device,
        abt,
        filters,
      ),
    });

    const highlightedProducts =
      topProducts.length >= 1 && allSponsoredData.highlightedProducts.length > 1
        ? allSponsoredData.highlightedProducts.filter(
            (hp) => hp.productId !== topProducts[0].productId,
          )
        : allSponsoredData.highlightedProducts;
    yield put<GetSponsoredDataHighlightedProductSuccess>({
      type: GET_SPONSORED_DATA_HIGHLIGHTED_PRODUCT_SUCCESS,
      highlightedProducts,
    });

    yield put<GetSponsoredDataPlaylistSuccess>({
      type: GET_SPONSORED_DATA_PLAYLIST_SUCCESS,
      sponsoredPlaylist: allSponsoredData.sponsoredPlaylist,
    });
  }
}

export default function* sponsoredDataSaga() {
  yield all([takeLatest(FETCH_SPONSORED_DATA, fetchSponsoredData)]);
}
